import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Box, Typography, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import analyticsService from '../services/analyticsService';

// Registrar los componentes necesarios de Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ClientsByAdvisorChart = ({ filters }) => {
  const [loading, setLoading] = useState(true);
  const [advisorData, setAdvisorData] = useState([]);
  const [error, setError] = useState(null);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await analyticsService.getClientsByAdvisor(filters);
        // Ordenar los datos por número de clientes (descendente)
        const sortedData = data.sort((a, b) => b.client_count - a.client_count);
        setAdvisorData(sortedData);
        setError(null);
      } catch (err) {
        console.error('Error al obtener datos de clientes por asesor:', err);
        setError('No se pudieron cargar los datos de clientes por asesor');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  // Limitar a los 10 primeros asesores para el gráfico
  const topAdvisors = advisorData.slice(0, 10);

  const chartData = {
    labels: topAdvisors.map(item => item.advisor_name),
    datasets: [
      {
        label: 'Número de clientes',
        data: topAdvisors.map(item => item.client_count),
        backgroundColor: 'rgba(255, 159, 64, 0.5)',
        borderColor: 'rgb(255, 159, 64)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Clientes Atendidos por Asesor (Top 10)',
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Número de Clientes'
        }
      }
    }
  };

  const toggleView = () => {
    setShowTable(!showTable);
  };

  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Clientes por Asesor
        </Typography>
        <Typography 
          variant="button" 
          sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
          onClick={toggleView}
        >
          {showTable ? 'Ver Gráfico' : 'Ver Tabla'}
        </Typography>
      </Box>
      
      <Box sx={{ height: 400, position: 'relative' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : advisorData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography>No hay datos disponibles para el período seleccionado</Typography>
          </Box>
        ) : showTable ? (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>Asesor</TableCell>
                  <TableCell align="right">Número de Clientes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {advisorData.map((row) => (
                  <TableRow key={row.advisor_id}>
                    <TableCell component="th" scope="row">
                      {row.advisor_name}
                    </TableCell>
                    <TableCell align="right">{row.client_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </Box>
    </Paper>
  );
};

export default ClientsByAdvisorChart;
